<!-- ── Atmospheric background motifs (purely decorative, behind content) ── -->
<div class="bg-motifs" aria-hidden="true">
  <img src="./assets/images/hex-pattern.svg" alt="" class="hex hex--left" fetchpriority="low" decoding="async" />
  <img src="./assets/images/hex-pattern.svg" alt="" class="hex hex--right" fetchpriority="low" decoding="async" />
  <picture>
    <source srcset="./assets/images/center_dark_mode_1100.webp" type="image/webp" />
    <img src="./assets/images/center_dark_mode.png" alt="" class="glow" fetchpriority="low" decoding="async" />
  </picture>
  <div class="motif-bottom-right"></div>
</div>

<!-- ── Hero ── -->
<section class="hero" id="hero">
  <div class="hero-grid">
    <div class="hero-copy">
      <div class="buzzwords">
        <h1 class="hero-title">{{ 'landing.buzzword1' | translate }}</h1>
        <p class="hero-subtitle">{{ 'landing.buzzword2' | translate }}</p>
      </div>
      <p-button
        [label]="'landing.getInTouch' | translate"
        class="start-button smartrecon-button"
        (onClick)="onGetInTouch()"
      />
    </div>

    <div class="panel-container hero-panel">
      <picture>
        <source
          srcset="
            ./assets/images/landing/hero_section_720.webp   720w,
            ./assets/images/landing/hero_section_1280.webp 1280w,
            ./assets/images/landing/hero_section.webp      2560w
          "
          sizes="(max-width: 900px) 90vw, 50vw"
          type="image/webp"
        />
        <img
          src="./assets/images/landing/hero_section.jpg"
          alt="Security operations centre at night"
          class="panel-image"
          fetchpriority="high"
          loading="eager"
          decoding="async"
        />
      </picture>
    </div>
  </div>

  <button
    type="button"
    class="scroll-indicator"
    (click)="scrollToContent()"
    (keyup.enter)="scrollToContent()"
    aria-label="Scroll to content"
  >
    <span class="scroll-indicator-inner">
      <span class="scroll-wheel"></span>
    </span>
  </button>
</section>

<!-- ── How it works ── -->
<section class="info-container" id="how-it-works">
  <h2 class="info-heading">{{ 'landing.mainHeading' | translate }}</h2>

  <div class="intro-grid">
    <p class="intro-text">{{ 'landing.intro1' | translate }}</p>
    <p class="intro-text">{{ 'landing.intro3' | translate }}</p>
    <p class="intro-text highlight">
      <strong>{{ 'landing.intro2' | translate }}</strong> {{ 'landing.intro2Continued' | translate }}
    </p>
  </div>

  <h3 class="process-heading">{{ 'landing.processHeading' | translate }}</h3>

  <ol class="steps">
    <li class="step" appFadeInOnView>
      <div class="step-text">
        <span class="step-num" aria-hidden="true">01</span>
        <h4 class="step-title">{{ 'landing.gatherTitle' | translate }}</h4>
        <p class="step-body">{{ 'landing.gatherText' | translate }}</p>
      </div>
      <div class="panel-container step-panel">
        <picture>
          <source
            srcset="
              ./assets/images/landing/AdobeStock_786717355_660.webp   660w,
              ./assets/images/landing/AdobeStock_786717355_1320.webp 1320w,
              ./assets/images/landing/AdobeStock_786717355.webp      2000w
            "
            sizes="(max-width: 900px) 90vw, 45vw"
            type="image/webp"
          />
          <img
            src="./assets/images/landing/AdobeStock_786717355.jpeg"
            alt="Analysts reviewing a global threat map"
            class="panel-image"
            loading="lazy"
            fetchpriority="low"
            decoding="async"
          />
        </picture>
      </div>
    </li>

    <li class="step" appFadeInOnView>
      <div class="step-text">
        <span class="step-num" aria-hidden="true">02</span>
        <h4 class="step-title">{{ 'landing.assessTitle' | translate }}</h4>
        <p class="step-body">{{ 'landing.assessText' | translate }}</p>
      </div>
      <div class="panel-container step-panel">
        <picture>
          <source
            srcset="
              ./assets/images/landing/AdobeStock_791155096_660.webp   660w,
              ./assets/images/landing/AdobeStock_791155096_1320.webp 1320w,
              ./assets/images/landing/AdobeStock_791155096.webp      2000w
            "
            sizes="(max-width: 900px) 90vw, 45vw"
            type="image/webp"
          />
          <img
            src="./assets/images/landing/AdobeStock_791155096.jpeg"
            alt="Network of linked exposure points"
            class="panel-image"
            loading="lazy"
            fetchpriority="low"
            decoding="async"
          />
        </picture>
      </div>
    </li>

    <li class="step" appFadeInOnView>
      <div class="step-text">
        <span class="step-num" aria-hidden="true">03</span>
        <h4 class="step-title">{{ 'landing.deliverTitle' | translate }}</h4>
        <p class="step-body">{{ 'landing.deliverText' | translate }}</p>
      </div>
      <div class="panel-container step-panel">
        <picture>
          <source
            srcset="
              ./assets/images/landing/deliver_660.webp   660w,
              ./assets/images/landing/deliver_1320.webp 1320w,
              ./assets/images/landing/deliver.webp      2560w
            "
            sizes="(max-width: 900px) 90vw, 45vw"
            type="image/webp"
          />
          <img
            src="./assets/images/landing/deliver.jpg"
            alt="Prioritised, risk-rated recommendations"
            class="panel-image"
            loading="lazy"
            fetchpriority="low"
            decoding="async"
          />
        </picture>
      </div>
    </li>
  </ol>
</section>

<!-- ── Pricing ── -->
<section class="pricing-wrapper" id="pricing" appFadeInOnView>
  @defer (on viewport) {
    <app-pricing-calculator />
  } @placeholder {
    <div class="pricing-placeholder" aria-hidden="true">
      <div class="pricing-placeholder-heading"></div>
      <div class="pricing-placeholder-card"></div>
    </div>
  }
</section>

<!-- ── Legal & ethical boundaries ── -->
<section class="legal-section">
  <h2 class="legal-heading">{{ 'landing.legalHeading' | translate }}</h2>
  <p class="legal-text">{{ 'landing.legalText' | translate }}</p>
</section>
